/**
 * Check whether a URL points to a video file.
 * Detects by file extension (.mp4) or data-URL MIME prefix (data:video/).
 */
export function isVideoUrl(url: string): boolean {
  if (!url) return false;

  // data:video/mp4;base64,... or similar
  if (url.startsWith('data:video/')) return true;

  // blob URLs can't be inspected by extension; fall through to false
  if (url.startsWith('blob:')) return false;

  try {
    const pathname = new URL(url).pathname.toLowerCase();
    return pathname.endsWith('.mp4');
  } catch {
    // Not a valid URL – try naive extension check
    return url.toLowerCase().endsWith('.mp4');
  }
}

/**
 * Check whether a URL is a YouTube video link.
 * Supports youtube.com/watch?v=... and youtu.be/... formats.
 */
export function isYouTubeUrl(url: string): boolean {
  if (!url) return false;
  return /^https?:\/\/(www\.)?(youtube\.com\/watch|youtu\.be\/)/.test(url);
}

/**
 * Extract the YouTube video ID from a YouTube URL.
 * Returns null if the URL is not a recognised YouTube link.
 */
export function extractYouTubeId(url: string): string | null {
  const watchMatch = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
  if (watchMatch) return watchMatch[1] ?? null;
  const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
  return shortMatch ? (shortMatch[1] ?? null) : null;
}

/**
 * Convert a YouTube watch URL to an embed URL.
 * Adds autoplay=1, mute=1, loop=1, and playlist=<id> so the video
 * loops automatically (required by the YouTube IFrame API).
 * Returns null if the video ID cannot be extracted.
 *
 * Pass `controls: false` to strip the player chrome (progress bar, CC,
 * settings, fullscreen, keyboard) — used for the small list tiles where the
 * video is a passive, non-interactive thumbnail and the controls just clutter it.
 */
export function getYouTubeEmbedUrl(
  url: string,
  options: { controls?: boolean } = {},
): string | null {
  const { controls = true } = options;
  const id = extractYouTubeId(url);
  if (!id) return null;
  const params = new URLSearchParams({
    autoplay: '1',
    mute: '1',
    loop: '1',
    playlist: id,
    rel: '0',
    modestbranding: '1',
  });
  if (!controls) {
    params.set('controls', '0');
    params.set('disablekb', '1');
    params.set('fs', '0');
    params.set('iv_load_policy', '3');
  }
  return `https://www.youtube.com/embed/${id}?${params.toString()}`;
}

/**
 * Matches an Instagram reel/post/tv permalink and captures the kind + shortcode.
 * Deliberately requires a /reel|reels|p|tv/{shortcode} path so plain profile
 * URLs (instagram.com/{username}) are NOT treated as embeddable media.
 */
const INSTAGRAM_URL_RE = /^https?:\/\/(?:www\.)?instagram\.com\/(reels?|p|tv)\/([A-Za-z0-9_-]+)/i;

/** Check whether a URL is an embeddable Instagram reel/post/tv link. */
export function isInstagramUrl(url: string): boolean {
  if (!url) return false;
  return INSTAGRAM_URL_RE.test(url);
}

/** Extract the Instagram shortcode (the bit after /reel|p|tv/). Null if not an IG media URL. */
export function extractInstagramShortcode(url: string): string | null {
  const m = url.match(INSTAGRAM_URL_RE);
  return m ? (m[2] ?? null) : null;
}

/**
 * Build the canonical Instagram embed URL (the iframe-able permalink) for a
 * reel/post/tv link, e.g. https://www.instagram.com/reel/{shortcode}/embed.
 * Returns null if the URL isn't a recognised Instagram media link.
 */
export function getInstagramEmbedUrl(url: string): string | null {
  const m = url.match(INSTAGRAM_URL_RE);
  if (!m) return null;
  const kind = m[1].toLowerCase().startsWith('reel') ? 'reel' : m[1].toLowerCase();
  return `https://www.instagram.com/${kind}/${m[2]}/embed`;
}
